/**
 * Blog article page + blog index. SEO-optimized.
 * Routes: /blog and /blog/:slug
 */
import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, User, ArrowRight, ArrowLeft } from 'lucide-react';
import { ARTICLES } from './data';

export const BlogIndex = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const articles = Object.values(ARTICLES).sort((a, b) => b.publishedDate.localeCompare(a.publishedDate));
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <Helmet>
        <title>FlavorFeed Blog — Restaurant POS, Marketing & Operations Insights</title>
        <meta name="description" content="Practical guides on restaurant POS, Apple Pay acceptance, ZATCA compliance, food truck operations, and more. Updated weekly." />
        <link rel="canonical" href="https://www.flavorfeed.ai/blog" />
      </Helmet>
      <section className="px-6 pt-20 pb-8">
        <div className="mx-auto max-w-5xl">
          <h1 className="text-4xl font-bold sm:text-5xl">FlavorFeed Blog</h1>
          <p className="mt-3 text-lg text-slate-300">Practical guides on restaurant tech, payments, compliance, and marketing.</p>
        </div>
      </section>
      <section className="px-6 py-8">
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={`/blog/${a.slug}`}
              className="group overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 transition hover:border-violet-500 hover:bg-slate-900"
              data-testid={`blog-card-${a.slug}`}
            >
              <div className="aspect-[16/9] overflow-hidden bg-gradient-to-br from-violet-900/60 to-fuchsia-900/40 relative">
                {a.heroImage ? (
                  <img
                    src={a.heroImage}
                    alt={a.title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : null}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
                <div className="absolute inset-0 flex items-end p-6">
                  <h2 className="text-xl font-bold leading-tight text-white group-hover:text-violet-200 drop-shadow-lg">
                    {a.title}
                  </h2>
                </div>
              </div>
              <div className="p-5">
                <p className="line-clamp-3 text-sm text-slate-300">{a.excerpt}</p>
                <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {a.publishedDate}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {a.readingMinutes} min read</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

const ArticlePage = () => {
  const { slug } = useParams();
  const article = ARTICLES[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!article) return <Navigate to="/blog" replace />;

  const canonical = `https://www.flavorfeed.ai/blog/${article.slug}`;
  const heroImageAbsolute = article.heroImage && article.heroImage.startsWith('http')
    ? article.heroImage
    : `https://www.flavorfeed.ai${article.heroImage || '/flavorfeed-logo-512.png'}`;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: heroImageAbsolute,
    author: { '@type': 'Organization', name: article.author },
    publisher: {
      '@type': 'Organization',
      name: 'FlavorFeed AI',
      logo: { '@type': 'ImageObject', url: 'https://www.flavorfeed.ai/flavorfeed-logo-512.png' },
    },
    datePublished: article.publishedDate,
    dateModified: article.updatedDate,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
  };
  const faqSchema = article.faqs && article.faqs.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: article.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  const related = Object.values(ARTICLES)
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <Helmet>
        <title>{article.metaTitle}</title>
        <meta name="description" content={article.metaDescription} />
        <meta name="keywords" content={article.tags.join(', ')} />
        <meta name="article:published_time" content={article.publishedDate} />
        <meta name="article:modified_time" content={article.updatedDate} />
        <meta name="article:author" content={article.author} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={article.metaTitle} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={heroImageAbsolute} />
        <meta property="og:image:width" content="1600" />
        <meta property="og:image:height" content="900" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.metaTitle} />
        <meta name="twitter:description" content={article.metaDescription} />
        <meta name="twitter:image" content={heroImageAbsolute} />
        <meta name="twitter:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <article className="px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> All articles
          </Link>
          <h1 className="mt-6 text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">{article.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-slate-400">
            <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" /> {article.author}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> <time dateTime={article.publishedDate}>{article.publishedDate}</time></span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {article.readingMinutes} min read</span>
            {article.updatedDate && article.updatedDate !== article.publishedDate && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-300"
                data-testid={`article-updated-ribbon-${article.slug}`}
              >
                Updated <time dateTime={article.updatedDate}>{new Date(article.updatedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</time>
              </span>
            )}
          </div>
          {article.heroImage && (
            <figure className="mt-8 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
              <img
                src={article.heroImage}
                alt={article.title}
                width="1600"
                height="900"
                loading="eager"
                decoding="async"
                className="h-auto w-full aspect-[16/9] object-cover"
                data-testid={`article-hero-image-${article.slug}`}
              />
            </figure>
          )}
          <div
            className="prose prose-lg prose-invert mt-10 max-w-none prose-headings:font-bold prose-h2:mt-12 prose-h2:text-3xl prose-h3:mt-8 prose-h3:text-xl prose-p:text-slate-200 prose-a:text-violet-300 prose-strong:text-white prose-table:text-sm prose-li:text-slate-200 prose-figure:my-10 prose-img:rounded-2xl prose-img:border prose-img:border-slate-800 prose-img:shadow-xl prose-img:shadow-violet-900/20 prose-figcaption:text-center prose-figcaption:text-sm prose-figcaption:text-slate-400 prose-figcaption:italic prose-figcaption:mt-3"
            dangerouslySetInnerHTML={{ __html: article.body }}
          />
          {article.faqs && article.faqs.length > 0 && (
            <section className="mt-16">
              <h2 className="text-2xl font-bold">Frequently asked</h2>
              <div className="mt-6 space-y-3">
                {article.faqs.map((f) => (
                  <details key={f.q} className="group rounded-xl border border-slate-800 bg-slate-900/40 p-5">
                    <summary className="cursor-pointer list-none font-semibold text-white">
                      <div className="flex items-start justify-between gap-4">
                        <span>{f.q}</span>
                        <span className="transition group-open:rotate-45 text-2xl leading-none text-slate-500">+</span>
                      </div>
                    </summary>
                    <p className="mt-3 text-slate-300 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      {/* Final CTA */}
      <section className="px-6 py-12">
        <div className="mx-auto max-w-3xl rounded-3xl border border-violet-500/40 bg-gradient-to-br from-violet-600/20 via-fuchsia-600/10 to-transparent p-10 text-center">
          <h2 className="text-3xl font-bold">Ready to modernize your restaurant?</h2>
          <p className="mt-3 text-slate-300">Start FlavorFeed free in 2 minutes. No credit card, no contract.</p>
          <Link
            to="/signup?plan=free"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 py-3.5 text-base font-semibold shadow-lg shadow-violet-900/50 transition hover:from-violet-500 hover:to-fuchsia-500"
            data-testid={`blog-${article.slug}-cta`}
          >
            Start Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Related */}
      <section className="border-t border-slate-800 px-6 py-12">
        <div className="mx-auto max-w-5xl">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Related reading</h3>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 transition hover:border-violet-500"
              >
                <h4 className="text-base font-semibold text-white">{a.title}</h4>
                <p className="mt-2 line-clamp-2 text-sm text-slate-400">{a.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticlePage;
